import { AnimatePresence, motion, useIsPresent } from "framer-motion"
import { type ReactNode, useLayoutEffect, useRef } from "react"
import { TRANSITION } from "@/shared/lib/motion"

/**
 * Уходящая страница пришпиливается к экрану там, где её видно, и гаснет поверх новой.
 * Пришпиливание нужно из-за прокрутки: новая страница открывается сверху (или там, где
 * человек был, если он вернулся назад), и в обычном потоке старая подпрыгнула бы вместе
 * с прокруткой ещё до того, как погасла. Эффект ребёнка срабатывает раньше эффекта
 * раскладки, который крутит страницу, поэтому замер берётся до прокрутки.
 */
function RouteFrame({ children }: { children: ReactNode }) {
	const ref = useRef<HTMLDivElement>(null)
	const isPresent = useIsPresent()
	useLayoutEffect(() => {
		const element = ref.current
		if (isPresent || !element) return
		const rect = element.getBoundingClientRect()
		Object.assign(element.style, {
			position: "fixed",
			top: `${rect.top}px`,
			left: `${rect.left}px`,
			width: `${rect.width}px`,
			pointerEvents: "none",
		})
	}, [isPresent])
	// Только opacity: transform на обёртке сломал бы `position: fixed` у потомков страницы.
	return (
		<motion.div
			ref={ref}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1, transition: TRANSITION.swapIn }}
			exit={{ opacity: 0, transition: TRANSITION.swapOut }}
		>
			{children}
		</motion.div>
	)
}

/**
 * Смена страниц перекрёстным переходом: старая гаснет поверх, новая проявляется на своём
 * месте. Без паузы между ними, в которой экран пустой: она читалась бы миганием.
 * Детям нужен застывший маршрут (`<Routes location>` или элемент из `useOutlet`), чтобы
 * уходящая страница до конца показывала себя, а не уже новую.
 */
export function RouteSwap({ id, children }: { id: string; children: ReactNode }) {
	return (
		<AnimatePresence initial={false}>
			<RouteFrame key={id}>{children}</RouteFrame>
		</AnimatePresence>
	)
}
